import type { ReactNode } from 'react';
import type {
  ProjectMetrics,
  RatingBucket,
  RatingTrendPoint,
  SurveyCounts,
  TrendStep,
} from '@blesk/contracts';
import { RUN_STATUS_LABEL } from '../../shared/labels';

/**
 * §14 ТЗ. Диаграммы разделов.
 *
 * Своя отрисовка на CSS и SVG, без графических библиотек: набор диаграмм
 * маленький и постоянный, а библиотека — это ещё одна зависимость в браузерном
 * бандле и отклонение от стека (§23).
 *
 * Ни одна диаграмма не считает показатели сама: приходят готовые числа
 * с сервера. Иначе «средняя оценка» на картинке разошлась бы с «средней
 * оценкой» в плитке рядом.
 */

const EMPTY = '—';

/** Русское склонение при числе: «1 ответ», «2 ответа», «5 ответов». */
export function plural(count: number, forms: [string, string, string]): string {
  const tens = count % 100;
  const ones = count % 10;
  const form =
    tens >= 11 && tens <= 14
      ? forms[2]
      : ones === 1
        ? forms[0]
        : ones >= 2 && ones <= 4
          ? forms[1]
          : forms[2];
  return `${count} ${form}`;
}

export function score(value: number | null): string {
  return value === null ? EMPTY : value.toFixed(2).replace('.', ',');
}

export function percent(value: number | null): string {
  return value === null ? EMPTY : `${(value * 100).toFixed(1).replace('.', ',')} %`;
}

/** Карточка диаграммы: заголовок, пояснение, содержимое. */
export function ChartCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <figure className="chart">
      <figcaption className="chart__head">
        <span className="chart__title">{title}</span>
        {hint && <span className="chart__hint">{hint}</span>}
      </figcaption>
      {children}
    </figure>
  );
}

/** Оценка 1–5 в цвет: негатив — красный, тройка — жёлтая, четыре и пять — акцент. */
function toneOfRating(value: number): string {
  if (value <= 2) return 'bad';
  if (value === 3) return 'warn';
  return 'good';
}

/**
 * Распределение оценок: пять столбцов, включая нулевые. Высота считается
 * от самой частой оценки, а не от суммы: на выборке из трёх ответов столбики
 * в один пиксель ничего не показывают.
 */
export function RatingColumns({
  distribution,
  answers,
}: {
  distribution: RatingBucket[];
  answers: number;
}) {
  const max = Math.max(1, ...distribution.map((bucket) => bucket.count));

  return (
    <div
      className="columns"
      role="img"
      aria-label={distribution
        .map((bucket) => `оценка ${bucket.value}: ${bucket.count}`)
        .join(', ')}
    >
      {distribution.map((bucket) => (
        <div className="columns__item" key={bucket.value}>
          <span className="columns__count">{bucket.count}</span>
          <span className="columns__track">
            {/* нулевую оценку не рисуем даже полоской: пустой столбец — тоже ответ */}
            {bucket.count > 0 && (
              <span
                className={`columns__fill columns__fill--${toneOfRating(bucket.value)}`}
                style={{ height: `${Math.round((bucket.count / max) * 100)}%` }}
              />
            )}
          </span>
          <span className="columns__label">{bucket.value}</span>
          <span className="columns__share">
            {answers > 0 ? `${Math.round((bucket.count / answers) * 100)} %` : EMPTY}
          </span>
        </div>
      ))}
    </div>
  );
}

// Система координат близка к тому, как диаграмма выводится на экране: сильно
// растянутый viewBox уменьшил бы подписи осей до нечитаемых (высоту ограничивает CSS)
const TREND_W = 520;
const TREND_H = 190;
const PAD = { top: 12, right: 10, bottom: 26, left: 26 };

/**
 * Календарная корзина динамики (§14, QA 22.08.2026 дефект 2).
 *
 * Точка — не период запуска (§9.2), а день, ISO-неделя или месяц отправки:
 * одна сетка на все расписания, подписи одного формата, никакие два отрезка
 * не накрывают друг друга. `bucket` — первый день корзины, 'ГГГГ-ММ-ДД'.
 */
export const TREND_STEP_HINT: Record<TrendStep, string> = {
  DAY: 'по дням отправки опросов',
  WEEK: 'по неделям отправки опросов',
  MONTH: 'по месяцам отправки опросов',
};

const MONTH_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

/** Подпись точки: «19.08» у дня и недели (неделя — своим понедельником), «авг 2026» у месяца. */
export function bucketLabel(bucket: string, step: TrendStep): string {
  const [year, month, day] = bucket.split('-');
  if (step === 'MONTH') return `${MONTH_SHORT[Number(month) - 1]} ${year}`;
  return `${day}.${month}`;
}

/** Полное имя корзины — для всплывающей подсказки и ссылок, где место есть. */
export function bucketTitle(bucket: string, step: TrendStep): string {
  const [year, month, day] = bucket.split('-');
  if (step === 'MONTH') return `${MONTH_SHORT[Number(month) - 1]} ${year}`;
  if (step === 'WEEK') return `неделя с ${day}.${month}.${year}`;
  return `${day}.${month}.${year}`;
}

/** Исключающая верхняя граница корзины — для фильтра «С…По» раздела (§13). */
export function bucketEndIso(bucket: string, step: TrendStep): string {
  const start = new Date(`${bucket}T00:00:00.000Z`);
  if (step === 'MONTH') {
    return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1)).toISOString();
  }
  start.setUTCDate(start.getUTCDate() + (step === 'WEEK' ? 7 : 1));
  return start.toISOString();
}

/**
 * Выключка подписи оси времени.
 *
 * Крайние точки стоят ровно на границах поля (`PAD.left` слева,
 * `TREND_W − PAD.right` справа), и подпись по центру вылезала за `viewBox`:
 * `2026-08-19` читалось как `026-08-19`, а `2026-W34` — как `2026-W`
 * (прогон QA 22.08.2026). Поэтому крайние прижимаются к краю, а не центрируются.
 * Единственная точка стоит посреди поля — упирать её некуда.
 */
function axisAnchor(index: number, count: number): 'start' | 'middle' | 'end' {
  if (count < 2) return 'middle';
  if (index === 0) return 'start';
  if (index === count - 1) return 'end';
  return 'middle';
}

/**
 * Динамика средней оценки по календарным корзинам (§14).
 *
 * Шкала жёстко 1–5, а не «от минимума до максимума»: подпилив ось под данные,
 * колебание в одну десятую можно нарисовать обвалом. Точки равноудалены —
 * корзины идут по порядку, а пустые (без единого опроса) не рисуются.
 */
export function TrendLine({ points, step }: { points: RatingTrendPoint[]; step: TrendStep }) {
  if (points.length === 0) return <p className="muted">Оценок за отбор нет.</p>;

  const innerW = TREND_W - PAD.left - PAD.right;
  const innerH = TREND_H - PAD.top - PAD.bottom;
  const x = (index: number) =>
    PAD.left + (points.length === 1 ? innerW / 2 : (index / (points.length - 1)) * innerW);
  const y = (value: number) => PAD.top + innerH - ((value - 1) / 4) * innerH;

  const line = points.map((point, index) => `${x(index)},${y(point.average)}`).join(' ');
  const area = `M ${x(0)},${PAD.top + innerH} L ${line.split(' ').join(' L ')} L ${x(points.length - 1)},${PAD.top + innerH} Z`;

  // Подписей по горизонтали не больше шести. При восьми крайняя левая, прижатая
  // к краю и растущая вправо, доставала до соседней: «2026-08-19» и «2026-08-20»
  // слипались в одну строку (прогон QA 22.08.2026, дефект 2).
  const labelStep = Math.ceil(points.length / 6);

  return (
    <svg
      className="trend"
      viewBox={`0 0 ${TREND_W} ${TREND_H}`}
      role="img"
      aria-label={`Средняя оценка ${TREND_STEP_HINT[step]}: ${points
        .map((point) => `${bucketTitle(point.bucket, step)} — ${score(point.average)}`)
        .join(', ')}`}
    >
      {[1, 2, 3, 4, 5].map((value) => (
        <g key={value}>
          <line
            className="trend__grid"
            x1={PAD.left}
            x2={TREND_W - PAD.right}
            y1={y(value)}
            y2={y(value)}
          />
          <text className="trend__axis" x={PAD.left - 8} y={y(value) + 4} textAnchor="end">
            {value}
          </text>
        </g>
      ))}

      {points.length > 1 && <path className="trend__area" d={area} />}
      {points.length > 1 && <polyline className="trend__line" points={line} />}

      {points.map((point, index) => (
        <g key={point.bucket}>
          <circle className="trend__dot" cx={x(index)} cy={y(point.average)} r={4}>
            <title>{`${bucketTitle(point.bucket, step)}: ${score(point.average)} по ${point.answers} ответам`}</title>
          </circle>
          {index % labelStep === 0 && (
            <text
              className="trend__axis"
              x={x(index)}
              y={TREND_H - 6}
              textAnchor={axisAnchor(index, points.length)}
            >
              {bucketLabel(point.bucket, step)}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

export type ProjectChartMetric = 'average' | 'responseRate';

/**
 * Разрез по объектам: горизонтальные полосы, худшее — сверху (§14).
 * Клик по названию сужает отбор до объекта — та же связка «показатель → список»,
 * что у плиток дашборда.
 */
export function ProjectBars({
  rows,
  metric,
  onPick,
}: {
  rows: ProjectMetrics[];
  metric: ProjectChartMetric;
  onPick?: (projectId: string) => void;
}) {
  // объекты без данных уходят вниз: «нет оценок» — не то же, что «плохие оценки»
  const sorted = [...rows].sort((left, right) => {
    const a = left[metric];
    const b = right[metric];
    if (a === null && b === null) return left.projectName.localeCompare(right.projectName, 'ru');
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  });

  // Оценка ложится на ту же шкалу, что и ось динамики: 1 — пусто, 5 — полная полоса.
  // Делить на 5 нельзя: единица — это худшая оценка, а не пятая часть хорошей.
  const share = (row: ProjectMetrics) => {
    const value = row[metric];
    if (value === null) return 0;
    return metric === 'average' ? (value - 1) / 4 : value;
  };
  const text = (row: ProjectMetrics) =>
    metric === 'average' ? score(row.average) : percent(row.responseRate);

  return (
    <ul className="bars" aria-label="Объекты отбора">
      {sorted.map((row) => (
        <li className="bars__row" key={row.projectId}>
          <span className="bars__name" title={row.projectName}>
            {onPick ? (
              <button type="button" className="link-button" onClick={() => onPick(row.projectId)}>
                {row.projectName}
              </button>
            ) : (
              row.projectName
            )}
          </span>
          <span className="bars__track">
            <span
              className={`bars__fill${metric === 'average' && row.average !== null && row.average <= 3 ? ' bars__fill--bad' : ''}`}
              style={{ width: `${Math.round(share(row) * 100)}%` }}
            />
          </span>
          <span className="bars__value">{text(row)}</span>
          <span className="bars__note muted">
            {plural(row.runs, ['запуск', 'запуска', 'запусков'])}
            {row.incidents > 0 ? `, негатив ${row.incidents}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Статусы запусков одной полосой: видно, чего в отборе больше, без таблицы. */
const STATUS_PARTS = [
  { key: 'completed', tone: 'good' },
  { key: 'sent', tone: 'accent' },
  { key: 'inProgress', tone: 'accent-soft' },
  { key: 'scheduled', tone: 'idle' },
  { key: 'expired', tone: 'bad' },
  { key: 'cancelled', tone: 'muted' },
] as const;

const STATUS_OF: Record<(typeof STATUS_PARTS)[number]['key'], keyof typeof RUN_STATUS_LABEL> = {
  completed: 'COMPLETED',
  sent: 'SENT',
  inProgress: 'IN_PROGRESS',
  scheduled: 'SCHEDULED',
  expired: 'EXPIRED',
  cancelled: 'CANCELLED',
};

export function StatusSplit({ counts }: { counts: SurveyCounts }) {
  if (counts.total === 0) return null;
  const parts = STATUS_PARTS.filter((part) => counts[part.key] > 0);

  return (
    <div className="split">
      <div
        className="split__bar"
        role="img"
        aria-label={parts
          .map((part) => `${RUN_STATUS_LABEL[STATUS_OF[part.key]]}: ${counts[part.key]}`)
          .join(', ')}
      >
        {parts.map((part) => (
          <span
            key={part.key}
            className={`split__part split__part--${part.tone}`}
            style={{ width: `${(counts[part.key] / counts.total) * 100}%` }}
            title={`${RUN_STATUS_LABEL[STATUS_OF[part.key]]}: ${counts[part.key]}`}
          />
        ))}
      </div>
      <ul className="split__legend">
        {parts.map((part) => (
          <li key={part.key}>
            <span className={`split__dot split__dot--${part.tone}`} aria-hidden="true" />
            {RUN_STATUS_LABEL[STATUS_OF[part.key]]} · {counts[part.key]}
          </li>
        ))}
      </ul>
    </div>
  );
}
